import { INotificationMessage } from "@fl/shared/src/types/messages";
import { UsuarioAttributes } from "@fl/shared/src/types/models/suporte_fl/usuarios";
import {
  ITableDataAction,
  ITableDataColumns,
} from "@fl/shared/src/types/tables";
import moment from "moment";
import { useEffect, useState } from "react";
import { FaCircle } from "react-icons/fa";
import { MdCheckCircle, MdOutlineCheckCircle } from "react-icons/md";
import {
  PiLockKey,
  PiLockKeyFill,
  PiLockKeyOpen,
  PiLockKeyOpenFill,
} from "react-icons/pi";
import {
  TbPlayCard1,
  TbPlayCard1Filled,
  TbPlayCardK,
  TbPlayCardKFilled,
} from "react-icons/tb";
import { TiCancel, TiCancelOutline } from "react-icons/ti";
import TableData from "../../../components/tables";
import ApiUsersPrivate from "../../../services/suporte/auth/users";
import StringsFuncs from "../../../utils/stringsFuncs";
import DefaultBasePage from "../../default/base";

const sizeIcons = 20;

const ConfigUsersPrivatePage = () => {
  const columns: ITableDataColumns[] = [
    {
      columnKey: "id_usuario",
      pk: true,
      label: "ID",
      alignText: "text-center",
    },
    {
      columnKey: "ativo",
      label: "Ativo",
      alignText: "text-center",
      render: (item: UsuarioAttributes) => {
        return <FaCircle color={item.ativo ? "green" : "red"} size={15} />;
      },
      sortValue: (item: UsuarioAttributes) => {
        return item.ativo;
      },
    },
    {
      columnKey: "banido",
      label: "Banido",
      alignText: "text-center",
      render: (item: UsuarioAttributes) => {
        return <FaCircle color={item.banido ? "green" : "red"} size={15} />;
      },
      sortValue: (item: UsuarioAttributes) => {
        return item.banido;
      },
    },
    {
      columnKey: "master",
      label: "Master",
      alignText: "text-center",
      render: (item: UsuarioAttributes) => {
        return <FaCircle color={item.master ? "green" : "red"} size={15} />;
      },
      sortValue: (item: UsuarioAttributes) => {
        return item.master;
      },
    },
    {
      columnKey: "nome",
      label: "Nome",
      render: (item: UsuarioAttributes) => {
        return new StringsFuncs(item.nome).cammelCase();
      },
    },
    {
      columnKey: "email",
      label: "E-mail",
    },
    {
      columnKey: "createdAt",
      label: "Data de Criação",
      alignText: "text-center",
      render: (item: UsuarioAttributes) => {
        return moment(item.createdAt).format("DD/MM/YYYY [às] HH:mm");
      },
      sortValue: (item: UsuarioAttributes) => {
        return item.createdAt;
      },
    },
    {
      columnKey: "actions",
      action: true,
      label: "Ações",
      actionButtons: (item: UsuarioAttributes) => {
        const id_user = item.id_usuario?.toString() || "";
        const buttons: ITableDataAction[] = [];

        if (item.ativo) {
          buttons.push({
            action: () => {
              active(id_user);
            },
            icon: <PiLockKey size={sizeIcons} />,
            hover: true,
            iconHover: <PiLockKeyFill size={sizeIcons} />,
            label: "Bloquear Usuário",
          });
        } else {
          buttons.push({
            action: () => {
              active(id_user);
            },
            icon: <PiLockKeyOpen size={sizeIcons} />,
            hover: true,
            iconHover: <PiLockKeyOpenFill size={sizeIcons} />,
            label: "Desbloquear Usuário",
          });
        }

        if (!item.banido) {
          buttons.push({
            action: () => {
              ban(id_user);
            },
            icon: <TiCancelOutline size={sizeIcons} />,
            hover: true,
            iconHover: <TiCancel size={sizeIcons} />,
            label: "Banir Usuário",
          });
        } else {
          buttons.push({
            action: () => {
              ban(id_user);
            },
            icon: <MdOutlineCheckCircle size={sizeIcons} />,
            hover: true,
            iconHover: <MdCheckCircle size={sizeIcons} />,
            label: "Remover Banição",
          });
        }

        if (item.master) {
          buttons.push({
            action: () => {
              master(id_user);
            },
            icon: <TbPlayCard1 size={sizeIcons} />,
            hover: true,
            iconHover: <TbPlayCard1Filled size={sizeIcons} />,
            label: "Tornar mais um...",
          });
        } else {
          buttons.push({
            action: () => {
              master(id_user);
            },
            icon: <TbPlayCardK size={sizeIcons} />,
            hover: true,
            iconHover: <TbPlayCardKFilled size={sizeIcons} />,
            label: "Be a King!",
          });
        }

        return buttons;
      },
    },
  ];

  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<INotificationMessage[]>([]);

  const [data, setData] = useState<UsuarioAttributes[]>([]);

  const active = async (id: string) => {
    try {
      setLoading(true);
      const result = await new ApiUsersPrivate().active(id);
      if (result.success) {
        const new_data = data.map((item) => {
          if (item.id_usuario?.toString() === id) {
            return result.data;
          } else {
            return item;
          }
        });
        setData(new_data);
      }
      if (result.message) setMessages(result.message);
    } finally {
      setLoading(false);
    }
  };

  const ban = async (id: string) => {
    try {
      setLoading(true);
      const result = await new ApiUsersPrivate().ban(id);
      if (result.success) {
        const new_data = data.map((item) => {
          if (item.id_usuario?.toString() === id) {
            return result.data;
          } else {
            return item;
          }
        });
        setData(new_data);
      }
      if (result.message) setMessages(result.message);
    } finally {
      setLoading(false);
    }
  };

  const master = async (id: string) => {
    try {
      setLoading(true);
      const result = await new ApiUsersPrivate().master(id);
      if (result.success) {
        const new_data = data.map((item) => {
          if (item.id_usuario?.toString() === id) {
            return result.data;
          } else {
            return item;
          }
        });
        setData(new_data);
      }
      if (result.message) setMessages(result.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      const result = await new ApiUsersPrivate().list();
      if (result.success) setData(result.data);

      if (result.message) setMessages(result.message);
    };

    loadData();
  }, []);

  return (
    <DefaultBasePage title="Usuários" loading={loading} message={messages}>
      <div className="text-justify">
        <TableData columns={columns} data={data} />
      </div>
    </DefaultBasePage>
  );
};

export default ConfigUsersPrivatePage;
